import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, Chip, Tooltip } from '@mui/material';
import { FirebaseServiceProvider } from './contexts/FirebaseServiceContext';
import Dashboard from './pages/Dashboard';
import Profile from './pages/profile';
import PrivacyPage from './pages/PrivacyPage';
import PublicPrivacyPage from './pages/PublicPrivacy';
import TrackingTransparency from './pages/TrackingTransparency';
// import FirebaseConnectionTest from './pages/FirebaseConnectionTest';
// import ImageDebugTool from './pages/ImageDebugTool';
import AuthContainer from './components/auth/AuthContainer';
import { auth, isEmulatorMode } from './firebase';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import CircularProgress from '@mui/material/CircularProgress';
import TestResults from './components/TestResults';
import TestResultDetail from './components/TestResultDetail';
import TestPage from './pages/TestPage';
import { Storage as StorageIcon } from '@mui/icons-material';
import FAQPage from './pages/FAQPage';
import DocumentationPage from './pages/DocumentationPage';
import AdminTools from './components/AdminTools';
import DataManagement from './pages/DataManagement';
import DataDeletionRequest from './pages/DataDeletionRequest';
import ProtectedRoute from './components/ProtectedRoute';
import { TermsAgreementProvider, useTermsAgreement } from './contexts/TermsAgreementContext';
import TermsAgreementDialog from './components/TermsAgreementDialog';
import PublicTermsOfService from './pages/PublicTermsOfService';
import { Toaster } from 'react-hot-toast';
import DatabaseTest from './components/DatabaseTest';
import SubscriptionDebugger from './components/SubscriptionDebugger';
import { isUserAdmin } from './utils/adminUtils';
import { initLogger } from './utils/loggerUtils';

// Test result detail page wrapper with URL parameter
const TestResultDetailPage = () => {
  const { testId } = useParams<{ testId: string }>();
  
  if (!testId) {
    return (
      <Container>
        <Typography color="error" variant="h6" sx={{ mt: 4 }}>
          Test ID not found
        </Typography>
      </Container>
    );
  }
  
  return (
    <Container>
      <TestResultDetail testId={testId} />
    </Container>
  );
};

// Test results page wrapper
const TestResultsPage = () => {
  return (
    <Container>
      <TestResults />
    </Container>
  );
};

// Types for the main navigation tabs
interface TabOption {
  label: string;
  path: string;
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00E5FF',
    },
    secondary: {
      main: '#FFEA00',
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#CCCCCC',
    },
    error: {
      main: '#FF5252',
    },
    action: {
      active: '#00E5FF',
      hover: 'rgba(0, 229, 255, 0.12)',
      selected: 'rgba(0, 229, 255, 0.20)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        outlined: {
          borderWidth: 2,
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
        }
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1E1E1E',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          border: '1px solid #333333',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: 'rgba(0, 229, 255, 0.12)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 500,
        }
      }
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
});

function AppContent() {
  const { currentUser, loading } = useAuth();
  const { hasAgreedToTerms, isLoading: termsLoading } = useTermsAgreement();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  
  const tabs: TabOption[] = [
    { label: 'Dashboard', path: '/' },
    { label: 'Take a Test', path: '/test' },
    { label: 'Profile', path: '/profile' }
  ];
  
  const getCurrentTabIndex = (): number => {
    const path = window.location.pathname;
    
    // Direct matches for exact paths
    if (path === '/profile') {
      return 2; // Profile tab index
    }
    if (path === '/test') {
      return 1; // Test tab index  
    }
    if (path === '/') {
      return 0; // Dashboard tab index
    }
    
    // Fallback with prefix matching
    const index = tabs.findIndex(tab => {
      // Match path prefix for detail pages (excluding root path)
      return tab.path !== '/' && path.startsWith(tab.path);
    });
    
    return index >= 0 ? index : 0; // Default to Dashboard tab if no match
  };
  
  const [activeTab, setActiveTab] = useState(getCurrentTabIndex());

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // Just update the tab and navigate, without triggering subscription checks
    setActiveTab(newValue);
    navigate(tabs[newValue].path);
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      sessionStorage.removeItem('wasLoggedIn');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    setActiveTab(getCurrentTabIndex());
  }, [location]);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        const adminStatus = await isUserAdmin();
        setIsAdmin(adminStatus);
      }
    };
    
    checkAdminStatus();
  }, [currentUser]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Check if current path is a public route
  const isPublicRoute = (path: string): boolean => {
    const publicRoutes = [
      '/public-privacy',
      '/terms-of-service',
      '/tracking-transparency',
      '/att',
      '/docs/user-guide',
      '/faq',
      '/data-deletion-request'
    ];
    return publicRoutes.includes(path) || publicRoutes.some(route => path.startsWith(route));
  };

  // If not logged in and trying to access a non-public route, show auth container
  if (!currentUser && !isPublicRoute(location.pathname)) {
    return <AuthContainer />;
  }

  // For public routes or authenticated users, show the appropriate component
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {currentUser && (
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              AmslerGridDaily
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {currentUser.email}
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => {
              navigate('/profile');
              setActiveTab(2); // Set to Profile tab index
            }}
            sx={{ 
              mr: 1,
              backgroundColor: 'rgba(0, 229, 255, 0.1)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(0, 229, 255, 0.2)',
              }
            }}
            aria-label="profile"
            size="small"
          >
            <PersonIcon />
          </IconButton>
          <Button 
            variant="outlined"
            color="primary"
            onClick={handleSignOut}
            startIcon={<LogoutIcon />}
            size="small"
            sx={{
              borderWidth: 2,
              fontWeight: 600
            }}
          >
            Logout
          </Button>
        </Toolbar>

        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          centered
          textColor="primary"
          indicatorColor="primary"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              '&.Mui-selected': {
                fontWeight: 700,
                color: theme.palette.primary.main
              }
            },
            '& .MuiTabs-indicator': {
              height: 3
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </AppBar>
      )}

      <Container sx={{ flex: 1, overflow: 'auto', py: 3 }}>
        <Routes>
          {/* Public routes - accessible without login */}
          <Route path="/public-privacy" element={<PublicPrivacyPage />} />
          <Route path="/privacy-policy" element={<PublicPrivacyPage />} />
          <Route path="/terms-of-service" element={<PublicTermsOfService />} />
          <Route path="/tracking-transparency" element={<TrackingTransparency />} />
          <Route path="/att" element={<TrackingTransparency />} />
          <Route path="/data-deletion-request" element={<DataDeletionRequest />} />
          <Route path="/docs/*" element={<DocumentationPage />} />
          <Route path="/docs/user-guide" element={<DocumentationPage />} />
          <Route path="/docs/terms-of-service" element={<DocumentationPage />} />
          <Route path="/docs/privacy-policy" element={<DocumentationPage />} />
          <Route path="/faq" element={
            <Box sx={{ maxWidth: 'lg', mx: 'auto', py: 4 }}>
              <FAQPage />
            </Box>
          } />

          {/* Protected routes - require login */}
          {currentUser ? (
            <>
              <Route path="/" element={<Dashboard />} />
              <Route path="/test-results" element={<TestResultsPage />} />
              <Route path="/test-results/:testId" element={
                <ProtectedRoute>
                  <TestResultDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/test" element={<TestPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/admin" element={<AdminTools />} />
              <Route
                path="/data-management"
                element={
                  <ProtectedRoute>
                    <DataManagement />
                  </ProtectedRoute>
                }
              />
              <Route path="/db-test" element={
                <ProtectedRoute>
                  <DatabaseTest />
                </ProtectedRoute>
              } />
              <Route path="/subscription-debugger" element={
                <ProtectedRoute>
                  {isAdmin ? <SubscriptionDebugger /> : <Navigate to="/" />}
                </ProtectedRoute>
              } />
            </>
          ) : null}
          
          {/* Redirect unauthorized access to login */}
          <Route path="*" element={!currentUser ? <AuthContainer /> : <Navigate to="/" />} />
        </Routes>
      </Container>

      {/* Only show terms dialog for logged in users on non-public routes */}
      {currentUser && !isPublicRoute(location.pathname) && (
        <TermsAgreementDialog
          open={!termsLoading && !hasAgreedToTerms}
          onClose={() => {}}
        />
      )}
    </Box>
  );
}

// Create a component for the emulator mode indicator
const EmulatorIndicator = () => {
  // Only show in development and when emulator mode is active
  if (process.env.NODE_ENV !== 'development' || !isEmulatorMode()) {
    return null;
  }

  return (
    <Tooltip title="Using Firebase Emulator - Data is not saved to production">
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
        }}
      >
        <Chip
          icon={<StorageIcon />}
          label="EMULATOR MODE"
          color="warning"
          variant="filled"
          sx={{
            fontWeight: 'bold',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(255, 167, 38, 0.7)' },
              '70%': { boxShadow: '0 0 0 6px rgba(255, 167, 38, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(255, 167, 38, 0)' }
            }
          }}
        />
      </Box>
    </Tooltip>
  );
};

function App() {
  // Initialize secure logger
  useEffect(() => {
    const initializeLogger = async () => {
      await initLogger();
    };
    
    initializeLogger();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FirebaseServiceProvider>
        <AuthProvider>
          <TermsAgreementProvider>
            <SubscriptionProvider>
              <Router>
                <AppContent />
                <EmulatorIndicator />
                <Toaster 
                  position="bottom-center"
                  toastOptions={{
                    style: {
                      background: '#333',
                      color: '#fff',
                      borderRadius: '8px',
                      padding: '12px 20px',
                    },
                    success: {
                      iconTheme: {
                        primary: '#00E5FF',
                        secondary: '#333',
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: '#FF5252',
                        secondary: '#333',
                      },
                    },
                  }}
                />
              </Router>
            </SubscriptionProvider>
          </TermsAgreementProvider>
        </AuthProvider>
      </FirebaseServiceProvider>
    </ThemeProvider>
  );
}

export default App; 